/**
 * Имя файла из заголовка `Content-Disposition`.
 *
 * Для кириллического имени Symfony шлёт ОБА параметра сразу:
 * `attachment; filename="Analiz-14.07.2026.zip"; filename*=utf-8''%D0%90...`
 * — ASCII-совместимый запасной вариант и точное имя по RFC 5987. Разбирать их
 * нужно по отдельности: жадное чтение «от `filename=` до конца строки» склеивало
 * оба параметра в одно имя вида `Analiz-...; filename*=utf-8Анализ-...`.
 *
 * Приоритет за `filename*`: это и есть настоящее имя, `filename` — его огрызок
 * для клиентов, не знающих RFC 5987.
 */
export function parseContentDispositionFileName(header: string | null | undefined): string {
  if (!header) {
    return '';
  }

  return extendedFileName(header) ?? plainFileName(header) ?? '';
}

/**
 * `filename*=<charset>'<lang>'<percent-encoded>` — значение обрывается на `;`
 * или конце строки: разделители внутри него обязаны быть закодированы.
 */
function extendedFileName(header: string): string | null {
  const match = header.match(/filename\*\s*=\s*([\w-]*)'[^']*'([^;]+)/i);

  if (!match) {
    return null;
  }

  const [, charset, value] = match;
  const encoded = value.trim();

  // Битую последовательность (`%D0` без пары) decodeURIComponent роняет —
  // тогда лучше запасной `filename`, чем упавшее скачивание.
  try {
    // ISO-8859-1 percent-байты decodeURIComponent прочитает как utf-8 и упадёт
    // либо соврёт. Такое имя честнее отдать запасному параметру.
    if (charset && charset.toLowerCase() !== 'utf-8') {
      return null;
    }

    return decodeURIComponent(encoded) || null;
  } catch {
    return null;
  }
}

/** `filename="имя с пробелами.zip"` либо `filename=имя.zip`. */
function plainFileName(header: string): string | null {
  const quoted = header.match(/filename\s*=\s*"([^"]*)"/i);

  if (quoted) {
    return quoted[1] || null;
  }

  const plain = header.match(/filename\s*=\s*([^;]+)/i);

  return plain ? plain[1].trim() || null : null;
}
